import { capitalize } from 'lodash';

import type {
  ClickAutocompleteSuggestions,
  AssociatedPartnerClass as Partner,
  Product,
  SuggestionSection,
  SuggestionState,
  ClickAutocompleteSuggestionsSuggestionType as SuggestionType,
} from '@coursera/event-pulse-types';

import type {
  DiscoveryCollection,
  DiscoveryCollectionsNonClipDegreeNorMasterTrackEntity,
} from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import { capitalizeAndTranslateEachWordInEntityType } from 'bundles/product-card/components/utils/productCardV2Utils';
import {
  ASK_ANYTHING_AI_PROMPT_ID,
  ASK_ANYTHING_ROUTE_TYPE,
  MAX_AI_PROMPTS_AS_YOU_TYPE,
  MAX_AS_YOU_TYPE_WITH_AI_PROMPTS_COUNT,
  ZERO_STATE_AUTOCOMPLETE_AI_PROMPTS_DISPLAY_LIMIT,
} from 'bundles/search-common/components/experiments/aiSearchOnLohp/constants';
import {
  LEARNING_PRODUCTS_ENUM_MAP,
  ON_SITE_SEARCH_PATH,
  RECENTLY_VIEWED_LOCAL_STORAGE_NAME,
  RECENT_SEARCHES_LOCAL_STORAGE_NAME,
  getTranslatedDifficulty,
} from 'bundles/search-common/constants';
import {
  AI_PROMPTS_ZERO_STATE_GROUP_ID,
  AS_YOU_TYPE_SEARCH_GROUP_ID,
  AUTOCOMPLETE_MODES,
  AUTOCOMPLETE_PHOTO_SIZE,
  AUTOCOMPLETE_PREFIX_DELIMITER,
  AUTOCOMPLETE_PRODUCT_TYPE_TO_ICON,
  AUTOCOMPLETE_PRODUCT_TYPE_TO_TRACKING_NAME,
  AUTOCOMPLETE_SECTION_PREFIXES,
  type AutocompleteMode,
  DIRECT_MATCHES_GROUP_ID,
  LEARNER_FAVORITES_GROUP_ID,
  ONBOARDING_GROUP_ID,
  RECENTLY_VIEWED_GROUP_ID,
  RECENT_SEARCHES_GROUP_ID,
  SearchesIcon,
  TRENDING_SEARCHES_GROUP_ID,
  ZERO_STATE_ITEMS_PER_SECTION,
  autocompletePrefixes,
  getAutocompleteProductTypePrettyName,
  getAutocompleteProductTypeSectionId,
  getTop10PartnerOptions,
} from 'bundles/search-common/constants/autocomplete';
import type { QueryPrompt } from 'bundles/search-common/providers/QueryPromptsProvider';
import type {
  SearchHit,
  SearchProductHit,
  SearchResult,
  SearchResults,
  SearchSuggestionHit,
} from 'bundles/search-common/providers/searchTypes';
import type { AutoCompleteHits, Hits, SuggestionHits } from 'bundles/search-common/types/autocomplete';
import {
  getItemsListFromLocalStorageByKeyAndTrimOutdated,
  getItemsWithTimestampsFromLocalStorage,
  getProductType,
} from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/search-common';

const TRENDING_RESULTS_LIMIT_MOBILE_TABLET = 8;
const TRENDING_RESULTS_LIMIT_DESKTOP = 12;

// helper type
type AutocompleteSection = Hits & { sectionTitle?: string | boolean; sectionId?: string; hits?: AutoCompleteHits };
type ReturnValue = AutocompleteSection[];
type SearchProductHitWithTracking = SearchProductHit & { indexName?: string | null };
type SearchSuggestionHitWithTracking = SearchSuggestionHit & { indexName?: string | null };
type HitWithTracking = (SearchProductHitWithTracking | SearchSuggestionHitWithTracking)[];

function filterUndefined<T>(item: T | undefined | null): item is T {
  return !!(item as T);
}

const formatTerms = (terms: (string | undefined)[]) => {
  if (!terms) return [];
  const sectionItems = terms.map((termName: string | undefined, termPosition: number) => ({
    name: termName,
    image: SearchesIcon,
    indexPosition: 0,
    objectID: `zero-state-data~${termName}`,
    hitPosition: termPosition,
  }));
  return sectionItems;
};

// Type guard to check if an entity has a difficulty level
function getDifficultyLevel(entity: DiscoveryCollectionsNonClipDegreeNorMasterTrackEntity | null | undefined): string {
  if (entity && typeof entity.difficultyLevel === 'string') {
    return capitalize(entity.difficultyLevel);
  } else {
    return '';
  }
}

export const getRecentlyViewedAutocompleteItems = (
  recentlyViewedCollection?: DiscoveryCollection | null
): AutocompleteItems => {
  const recentlyViewedProducts = getItemsListFromLocalStorageByKeyAndTrimOutdated(RECENTLY_VIEWED_LOCAL_STORAGE_NAME);
  const products = [];

  // Helper function to convert stored productType (human-readable) to enum key format for LEARNING_PRODUCTS_ENUM_MAP
  const convertProductTypeToEnumKey = (productType: string): string => {
    if (!productType) return '';
    // Map human-readable format to enum key format (used as keys in LEARNING_PRODUCTS_ENUM_MAP)
    const productTypeMap: Record<string, string> = {
      Course: 'COURSE',
      'Guided Project': 'GUIDED_PROJECT',
      Project: 'PROJECT',
      Specialization: 'SPECIALIZATION',
      'Professional Certificate': 'PROFESSIONAL_CERTIFICATE',
      Degree: 'DEGREE',
      'University Certificate': 'UNIVERSITY_CERTIFICATE',
      Mastertrack: 'MASTERTRACK',
      'Postgraduate Diploma': 'POSTGRADUATE_DIPLOMA',
      'Graduate Certificate': 'GRADUATE_CERTIFICATE',
      Video: 'VIDEO',
      Lesson: 'LESSON',
    };
    return productTypeMap[productType] || productType.toUpperCase().replace(/\s+/g, '_');
  };

  const localRecentlyViewedProducts = recentlyViewedProducts.map((recentItem) => {
    const storedProductType = recentItem.productType ?? '';
    const storedDifficulty = recentItem.difficulty ?? '';

    // Convert stored productType to enum key format for use with LEARNING_PRODUCTS_ENUM_MAP
    const productTypeEnumKey = convertProductTypeToEnumKey(storedProductType);

    // Use getTranslatedDifficulty for difficulty, similar to SearchCards
    const difficulty = storedDifficulty ? getTranslatedDifficulty(storedDifficulty) : '';

    const supportText = [recentItem.partnerName, storedProductType, storedDifficulty].filter(Boolean).join(' • ');

    return {
      id: `${AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED}${recentItem.name}` || '',
      name: recentItem.name || '',
      imageUrl: recentItem.partnerLogo || '',
      cardImageUrl: recentItem.cardImageUrl || '',
      supportText: supportText || '',
      path: recentItem.path || '',
      eventProductId: recentItem.id,
      difficulty,
      productType: productTypeEnumKey, // Store the enum key format for use with LEARNING_PRODUCTS_ENUM_MAP
      partnerName: recentItem.partnerName || '',
      isRecentlyViewed: true,
    };
  });
  products.push(...localRecentlyViewedProducts);
  // If there are fewer than 4 recently viewed products, append products from recentlyViewedCollection
  // collection lags so we need to use local storage for most recent viewed product
  if (products.length < 4 && recentlyViewedCollection?.entities) {
    const existingProductNames = new Set(products.map((product) => product.name));

    const additionalProducts = recentlyViewedCollection.entities
      .filter((entity) => entity && !existingProductNames.has(entity.name))
      .slice(0, 4 - products.length)
      .map((entity) => {
        const difficultyLevel = getDifficultyLevel(entity as DiscoveryCollectionsNonClipDegreeNorMasterTrackEntity);
        const marketingProductType = getProductType(
          (entity as DiscoveryCollectionsNonClipDegreeNorMasterTrackEntity)?.productCard?.marketingProductType as string
        );

        const supportText = [
          entity?.partners?.map((partner) => partner?.name).join(', '),
          marketingProductType,
          difficultyLevel,
        ]
          .filter(Boolean)
          .join(' • ');
        return {
          id: `${AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED}${entity?.name}` || '',
          name: entity?.name || '',
          imageUrl: entity?.partners ? entity?.partners[0]?.logo || '' : '',
          cardImageUrl: entity?.imageUrl || '',
          supportText: supportText || '',
          path: entity?.url || '',
          eventProductId: entity?.id,
          difficulty: difficultyLevel,
          productType: convertProductTypeToEnumKey(marketingProductType),
          partnerName: entity?.partners?.map((partner) => partner?.name).join(', '),
          isRecentlyViewed: true,
        };
      });

    products.push(...additionalProducts);
  }
  return products.slice(0, 4);
};
export const getRecentlySearchedAutocompleteItems = (): AutocompleteItems => {
  const recentlySearchedItems = getItemsWithTimestampsFromLocalStorage(RECENT_SEARCHES_LOCAL_STORAGE_NAME);
  return recentlySearchedItems
    .map((item) => ({
      id: `${AUTOCOMPLETE_SECTION_PREFIXES.RECENT_SEARCHES}${item.suggestion}`,
      name: item.suggestion,
      ...(item.isAiPrompt && { isAiPrompt: true }),
      ...(item.routeType && { routeType: item.routeType }),
      ...(item.intentType && { intentType: item.intentType }),
    }))
    .slice(0, 5);
};

const getRecentlyViewedSectionItems = () => {
  const recentlyViewedProducts = getItemsListFromLocalStorageByKeyAndTrimOutdated(RECENTLY_VIEWED_LOCAL_STORAGE_NAME);
  if (recentlyViewedProducts.length === 0) return [];
  const sectionItems = recentlyViewedProducts.map((recentItem, itemPosition) => ({
    name: recentItem.name,
    partners: [recentItem.partnerName],
    objectUrl: recentItem.path,
    image: recentItem.image,
    indexPosition: 1,
    objectID: `zero-state-data~${recentItem.name}`,
    hitPosition: itemPosition,
  }));
  return sectionItems.slice(0, ZERO_STATE_ITEMS_PER_SECTION);
};

export const getFirstSectionData = (
  collectionRecommendations?: DiscoveryCollection | null
): AutocompleteSection | undefined => {
  const moduleID = collectionRecommendations?.id || '';
  const getSectionTitle = (id: string) => {
    let translatedSectionTitle = '';
    if (id.includes('recent-views')) translatedSectionTitle = _t('Based on your searches');
    if (id.includes('cold-start')) translatedSectionTitle = _t('Recommended for you');
    if (id.includes('trendingByEnrollmentsNumericTag')) translatedSectionTitle = _t('Most Popular Specializations');
    return translatedSectionTitle;
  };
  const getSectionId = (id: string) => {
    let sectionId = '';
    if (id.includes('recent-views')) sectionId = 'recently_viewed';
    if (id.includes('cold-start')) sectionId = 'suggested_searches';
    if (id.includes('trendingByEnrollmentsNumericTag')) sectionId = 'most_popular_specializations';
    return sectionId;
  };
  const inputRecommendationsData = moduleID.includes('trendingByEnrollmentsNumericTag')
    ? collectionRecommendations?.entities?.filter(
        (e) =>
          e?.__typename === 'DiscoveryCollections_specialization' ||
          e?.__typename === 'DiscoveryCollections_professionalCertificate'
      )
    : collectionRecommendations?.entities;
  const recentlyViewedData = getRecentlyViewedSectionItems();
  const { sectionTitle, sectionItems, sectionId } =
    recentlyViewedData.length > 0
      ? {
          sectionTitle: _t('Recently viewed'),
          sectionId: 'recently_viewed',
          sectionItems: recentlyViewedData as Partial<Hits>[],
        }
      : {
          sectionTitle: getSectionTitle(moduleID),
          sectionId: getSectionId(moduleID),
          sectionItems: inputRecommendationsData
            ?.filter((rec) => !!rec)
            .map((rec) => ({
              ...rec,
              image: { imageUrl: rec?.imageUrl, size: AUTOCOMPLETE_PHOTO_SIZE },
              partners: rec?.partners?.map((p) => p?.name),
              objectUrl: rec?.url,
            })) as Partial<Hits>[],
        };
  if (sectionItems) {
    return {
      sectionTitle,
      hits: sectionItems
        .slice(0, ZERO_STATE_ITEMS_PER_SECTION)
        .map(
          (suggestion) =>
            ({ ...suggestion, sectionTitle, sectionId, trackingName: 'zero_state_product' } as SuggestionHits)
        ),
    } as AutocompleteSection;
  }
  return undefined;
};

const getSecondSectionData = (suggestionHits?: SearchSuggestionHit[]): AutocompleteSection | undefined => {
  const recentSearches = getItemsListFromLocalStorageByKeyAndTrimOutdated(RECENT_SEARCHES_LOCAL_STORAGE_NAME);
  const { sectionItems, sectionTitle, sectionId } =
    recentSearches.length > 0
      ? {
          sectionItems: formatTerms(recentSearches),
          sectionTitle: _t('Recent Searches'),
          sectionId: 'recent_searches',
        }
      : {
          sectionItems:
            suggestionHits &&
            formatTerms(suggestionHits.map((term: SearchSuggestionHit) => term.name).filter(filterUndefined)),
          sectionTitle: _t('Popular right now'),
          sectionId: 'popular_right_now',
        };

  if (sectionItems) {
    return {
      sectionTitle,
      hits: sectionItems.slice(0, ZERO_STATE_ITEMS_PER_SECTION).map(
        (suggestion) =>
          ({
            ...suggestion,
            sectionTitle,
            sectionId,
            trackingName: 'zero_state_suggestion',
          } as SuggestionHits)
      ),
    } as AutocompleteSection;
  }
  return undefined;
};

const getSearchZeroStateSections = (
  collectionRecommendations?: DiscoveryCollection | null,
  suggestionHits?: SearchSuggestionHit[]
): ReturnValue => {
  return [getFirstSectionData(collectionRecommendations), getSecondSectionData(suggestionHits)].filter(filterUndefined);
};

const getSectionTitle = (hits: HitWithTracking) => {
  return hits[0]?.__typename !== 'Search_ProductHit'
    ? getAutocompleteProductTypePrettyName()[hits[0]?.__typename || 'Search_SuggestionHit']
    : undefined;
};

const getSectionId = (hits: HitWithTracking) => {
  return hits[0]?.__typename !== 'Search_ProductHit'
    ? getAutocompleteProductTypeSectionId()[hits[0]?.__typename || 'Search_SuggestionHit']
    : undefined;
};
const mapSearchSuggestionHits = (hits: HitWithTracking, indexPosition = 0): AutoCompleteHits => {
  const sectionTitle = getSectionTitle(hits);
  const sectionId = getSectionId(hits);
  return hits?.map(
    (hit, hitPosition: number): SuggestionHits => ({
      ...hit,
      id: hit.id || undefined,
      name: hit.name || undefined,
      partners: (hit?.__typename === 'Search_ProductHit' && hit?.partners) || undefined,
      hitPosition,
      indexName: hit.indexName || '',
      indexPosition,
      tagline: (hit?.__typename === 'Search_ProductHit' && hit.tagline) || '',
      objectUrl: (hit?.__typename === 'Search_ProductHit' && hit?.url) || '',
      trackingName: AUTOCOMPLETE_PRODUCT_TYPE_TO_TRACKING_NAME[hit?.__typename || 'Search_ProductHit'],
      imageUrl: hit?.__typename === 'Search_ProductHit' ? hit?.imageUrl || undefined : undefined,
      image:
        hit?.__typename === 'Search_ProductHit'
          ? { imageUrl: hit?.imageUrl || undefined, size: AUTOCOMPLETE_PHOTO_SIZE }
          : AUTOCOMPLETE_PRODUCT_TYPE_TO_ICON[hit?.__typename || 'Search_SuggestionHit'],
      sectionTitle,
      sectionId,
    })
  );
};

const mapSearchSuggestionSection = (hits: HitWithTracking, indexPosition = 0): AutocompleteSection | undefined => {
  const rawHits = hits[0];
  const sectionTitle = getSectionTitle(hits);
  if (!rawHits) return undefined;
  const suggestionHits = mapSearchSuggestionHits(hits, indexPosition);
  const firstHit = suggestionHits[0];
  if (!firstHit) return undefined;

  return {
    // the item below is just to comply with the weird typing of react-autosuggest
    ...firstHit,
    index: '',
    name: '',
    objectUrl: '',
    trackingName: '',
    indexName: '',
    image: { size: 16, ...firstHit.image },
    partners: firstHit.partners || [],
    tagline: firstHit.tagline || '',
    objectID: firstHit.objectID || '',
    hitPosition: firstHit.hitPosition || 0,
    indexPosition: firstHit.indexPosition || 0,
    sectionTitle,
    // If it's index used for direct match, we just show one result on this section
    hits: suggestionHits,
  };
};

const getSearchSuggestions = (
  query: string,
  hits: SearchProductHitWithTracking[] = [],
  suggestionHits: SearchSuggestionHitWithTracking[] = []
): ReturnValue => {
  // For each index, take the first n hits according to the config
  // Product spec is to show direct match if user typed more than 3 characters
  const shouldShowDirectMatch = query.length >= 3;
  if (shouldShowDirectMatch) {
    const mainSection = mapSearchSuggestionSection(hits, 0);
    const suggestionSection = mapSearchSuggestionSection(suggestionHits, 1);
    return [mainSection, suggestionSection].filter(filterUndefined);
  } else {
    const suggestionSection = mapSearchSuggestionSection(suggestionHits, 0);
    return [suggestionSection].filter(filterUndefined);
  }
};

export function getAutoCompleteSections(
  query: string,
  mainSearchResult?: SearchResult,
  suggestionsData?: SearchResult,
  collectionRecommendations?: DiscoveryCollection | null
): ReturnValue {
  const { elements, source } = mainSearchResult || {};
  const indexName = source?.indexName;
  const hits = (elements as SearchProductHit[])?.map((hit: SearchProductHit) => ({ ...hit, indexName }));

  const { elements: suggestionElements, source: suggestionSource } = suggestionsData || {};
  const suggestionIndexName = suggestionSource?.indexName;
  const suggestionHits = (suggestionElements as SearchSuggestionHit[])?.map((hit: SearchSuggestionHit) => ({
    ...hit,
    indexName: suggestionIndexName,
  }));

  if (elements?.length === 0 || query.length < 1) {
    return getSearchZeroStateSections(collectionRecommendations, suggestionHits);
  } else {
    return getSearchSuggestions(query, hits, suggestionHits);
  }
}

export type AutocompleteItem = {
  id: string;
  name: string;
  imageUrl?: string;
  supportText?: string;
  path?: string;
  textValue?: string;
  eventProductId?: string;
  difficulty?: string;
  productType?: string;
  cardImageUrl?: string;
  partnerName?: string;
  isRecentlyViewed?: boolean;
  routeType?: string;
  routeParameter?: string;
  intentType?: string;
  isAiPrompt?: boolean;
  suggestionIndex?: number;
};

export type AutocompleteItems = AutocompleteItem[];

export type AutocompleteItemGroup = {
  id: string;
  title: string;
  children: AutocompleteItems;
};

export const mapSearchSuggestionHitsToAutocompleteItems = (
  elements: SearchHit[],
  groupId: string
): AutocompleteItems => {
  const prefix =
    groupId === AS_YOU_TYPE_SEARCH_GROUP_ID
      ? AUTOCOMPLETE_SECTION_PREFIXES.AS_YOU_TYPE_SEARCH
      : AUTOCOMPLETE_SECTION_PREFIXES.TRENDING_SEARCHES;
  return elements
    .map(
      (item) =>
        item.name &&
        item.id && {
          id: `${prefix}${item.name.toLowerCase()}`,
          name: item.name,
        }
    )
    .filter(Boolean);
};

export const mapSearchHitProductsToAutocompleteItems = (elements: SearchHit[]): AutocompleteItems => {
  return elements
    .map((hit) => {
      if (hit.__typename !== 'Search_ProductHit') return null;
      if (!hit.name || !hit.url) return null;

      const supportText = [
        hit.partners?.map((partner) => partner).join(', '),
        capitalizeAndTranslateEachWordInEntityType(LEARNING_PRODUCTS_ENUM_MAP[hit.productType ?? '']),
        getTranslatedDifficulty(hit.productDifficultyLevel ?? ''),
      ]
        .filter(Boolean)
        .join(' • ');

      return {
        id: `${AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES}${hit.name}`,
        name: hit.name ?? '',
        imageUrl: hit.partnerLogos?.[0] ?? hit.imageUrl ?? '',
        cardImageUrl: hit.imageUrl ?? '',
        supportText,
        eventProductId: hit.id ?? '',
        path: hit.url,
        productType: hit.productType ?? '',
        difficulty: hit.productDifficultyLevel ?? '',
        partnerName: hit.partners?.[0] ?? '',
        isRecentlyViewed: false,
      };
    })
    .filter(Boolean) as AutocompleteItems;
};

export function getSearchUpdatedUrl(searchText: string = '', query: Record<string, string>) {
  const params = new URLSearchParams(query);
  params.set('query', searchText);
  return ON_SITE_SEARCH_PATH + '?' + params.toString().replace(/\+/g, '%20');
}

export function getOnboardingAutocompleteItems(onboardingUrl: string): AutocompleteItems {
  return [
    {
      id: `${AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING}~${onboardingUrl}`,
      name: _t('Not sure where to start?'),
      textValue: _t('Take a short quiz'),
      path: onboardingUrl,
    },
  ];
}

//* infer mode from query and recent items */
export function inferAutocompleteMode(
  query: string,
  recentlySearchedItems: AutocompleteItems,
  recentlyViewedItems: AutocompleteItems
): AutocompleteMode {
  // if there's a query, we should show the "as you type" state
  if (query.length) {
    return AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE;
  }
  // otherwise, show zero state (which will include recent searches/viewed items)
  return AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE;
}

export const isExactPartnerNameMatch = (query: string) =>
  getTop10PartnerOptions().some((partner) => partner.name === query);

export function createRepeatSearchAutocompleteItems(
  recentlySearchedItems: AutocompleteItems,
  recentlyViewedData: AutocompleteItems,
  trendingSuggestions: SearchResults | undefined,
  isMobileOrTabletView?: boolean,
  onboardingUrl?: string,
  isUserLoggedIn?: boolean
): AutocompleteItemGroup[] {
  const repeatSearchResults = [];
  if (recentlySearchedItems.length > 0) {
    repeatSearchResults.push({
      id: RECENT_SEARCHES_GROUP_ID,
      title: _t('Recent searches'),
      children: recentlySearchedItems.slice(0, 3),
    });
  }

  const trendingLimit = isMobileOrTabletView ? TRENDING_RESULTS_LIMIT_MOBILE_TABLET : TRENDING_RESULTS_LIMIT_DESKTOP;
  repeatSearchResults.push({
    id: TRENDING_SEARCHES_GROUP_ID,
    title: _t('Trending on Coursera'),
    children: mapSearchSuggestionHitsToAutocompleteItems(
      trendingSuggestions?.[0]?.elements.slice(0, trendingLimit) || [],
      TRENDING_SEARCHES_GROUP_ID
    ),
  });
  if (recentlyViewedData.length > 0) {
    const hasFourOrMore = recentlyViewedData.length >= 4;
    repeatSearchResults.push({
      id: hasFourOrMore ? RECENTLY_VIEWED_GROUP_ID : LEARNER_FAVORITES_GROUP_ID,
      title: hasFourOrMore ? _t('Recently viewed') : _t('Start with these learner favorites'),
      children: recentlyViewedData,
    });
  }

  if (!isUserLoggedIn && onboardingUrl) {
    const onboardingItems = getOnboardingAutocompleteItems(onboardingUrl);
    repeatSearchResults.push({
      id: ONBOARDING_GROUP_ID,
      title: '',
      children: onboardingItems,
    });
  }

  return repeatSearchResults;
}

export function isLoosePartnerNameMatch(partnerName: string, searchText: string): boolean {
  const lowerCaseSearchText = searchText.toLowerCase().trim();
  const lowerCasePartnerName = partnerName.toLowerCase();
  if (!lowerCasePartnerName || !lowerCaseSearchText) return false;
  const isExactMatchFromStart = lowerCasePartnerName.startsWith(lowerCaseSearchText);
  const hasWordMatch = lowerCasePartnerName.split(' ').some((word) => word.startsWith(lowerCaseSearchText));
  return isExactMatchFromStart || hasWordMatch;
}

/** Maps query prompts to autocomplete items for the AI prompts group. */
export const mapQueryPromptsToAutocompleteItems = (
  prompts: QueryPrompt[],
  prefix: string,
  useShortText = false
): AutocompleteItems => {
  return prompts.map((prompt) => {
    const text = useShortText ? prompt.promptTextShort : prompt.promptTextLong;
    return {
      id: `${prefix}${prompt.id}`,
      name: text,
      textValue: text,
      routeType: prompt.routeType,
      routeParameter: prompt.routeParameter,
      intentType: prompt.intentType,
      isAiPrompt: true,
    };
  });
};

export function createAsYouTypeAutocompleteItems(
  mainSearchDataElements: SearchHit[] | undefined,
  onboardingUrl?: string,
  isUserLoggedIn?: boolean,
  productHits?: SearchHit[] | undefined,
  query?: string,
  aiPrompts?: QueryPrompt[]
): AutocompleteItemGroup[] {
  const groups: AutocompleteItemGroup[] = [];

  // Show direct match products if query is 3+ characters (matches legacy behavior)
  const shouldShowDirectMatch = query && query.length >= 3 && productHits && productHits.length > 0;

  if (shouldShowDirectMatch && productHits) {
    const directMatchProducts = mapSearchHitProductsToAutocompleteItems(productHits);

    if (directMatchProducts.length > 0) {
      groups.push({
        id: DIRECT_MATCHES_GROUP_ID,
        title: '',
        children: directMatchProducts.slice(0, 1),
      });
    }
  }

  // Add search suggestions
  const autocompleteOptions = mapSearchSuggestionHitsToAutocompleteItems(
    mainSearchDataElements || [],
    AS_YOU_TYPE_SEARCH_GROUP_ID
  );

  // If no results from backend and query exists, add the query itself as an option
  const finalOptions =
    autocompleteOptions.length === 0 && query && query.trim()
      ? [
          {
            id: `${AUTOCOMPLETE_SECTION_PREFIXES.AS_YOU_TYPE_SEARCH}${query.toLowerCase()}`,
            name: query,
          },
        ]
      : autocompleteOptions;

  // Append AI prompts to the same group so there's no visual gap.
  // If the feature is enabled (aiPrompts array provided) but no suggestions returned, show "Ask anything" as fallback.
  const hasAiPrompts = aiPrompts && aiPrompts.length > 0;
  const askAnythingFallback = {
    id: ASK_ANYTHING_AI_PROMPT_ID,
    name: _t('Ask anything'),
    textValue: _t('Ask anything'),
    routeType: ASK_ANYTHING_ROUTE_TYPE,
    isAiPrompt: true,
  };
  let aiPromptItems: AutocompleteItems = [];
  if (hasAiPrompts) {
    aiPromptItems = mapQueryPromptsToAutocompleteItems(
      aiPrompts,
      AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_AS_YOU_TYPE
    ).slice(0, MAX_AI_PROMPTS_AS_YOU_TYPE);
  } else if (aiPrompts !== undefined) {
    // Feature is enabled but no suggestions returned — show Ask anything as fallback
    aiPromptItems = [askAnythingFallback];
  }

  const searchSuggestions = hasAiPrompts
    ? finalOptions.slice(0, MAX_AS_YOU_TYPE_WITH_AI_PROMPTS_COUNT - aiPromptItems.length)
    : finalOptions;

  const asYouTypeChildren = [...aiPromptItems, ...searchSuggestions];

  groups.push({
    id: AS_YOU_TYPE_SEARCH_GROUP_ID,
    title: '',
    children: asYouTypeChildren,
  });

  if (!isUserLoggedIn && onboardingUrl) {
    const onboardingItems = getOnboardingAutocompleteItems(onboardingUrl);
    groups.push({
      id: ONBOARDING_GROUP_ID,
      title: '',
      children: onboardingItems,
    });
  }

  return groups;
}

type CreateZeroStateAutocompleteItemsOptions = {
  trendingSuggestions?: SearchResults;
  isMobileOrTabletView?: boolean;
  onboardingUrl?: string;
  isUserLoggedIn?: boolean;
  recentlySearchedItems?: AutocompleteItems;
  recentlyViewedItems?: AutocompleteItems;
  screenWidth?: number;
  trendingProducts?: SearchResults;
  aiPrompts?: QueryPrompt[];
};

export function createZeroStateAutocompleteItems({
  trendingSuggestions,
  isMobileOrTabletView,
  onboardingUrl,
  isUserLoggedIn,
  recentlySearchedItems,
  recentlyViewedItems,
  screenWidth,
  trendingProducts,
  aiPrompts,
}: CreateZeroStateAutocompleteItemsOptions = {}): AutocompleteItemGroup[] {
  const groups: AutocompleteItemGroup[] = [];

  let aiPromptsGroup: AutocompleteItemGroup | undefined;
  if (aiPrompts && aiPrompts.length > 0) {
    const allItems = mapQueryPromptsToAutocompleteItems(
      aiPrompts,
      AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_ZERO_STATE,
      true
    );
    const aiPromptItems = [
      ...allItems.slice(0, ZERO_STATE_AUTOCOMPLETE_AI_PROMPTS_DISPLAY_LIMIT),
      {
        id: ASK_ANYTHING_AI_PROMPT_ID,
        name: _t('Ask anything'),
        textValue: _t('Ask anything'),
        routeType: ASK_ANYTHING_ROUTE_TYPE,
        isAiPrompt: true,
      },
    ];
    aiPromptsGroup = {
      id: AI_PROMPTS_ZERO_STATE_GROUP_ID,
      title: _t('Try asking about'),
      children: aiPromptItems,
    };
  }

  if (recentlySearchedItems && recentlySearchedItems.length > 0) {
    groups.push({
      id: RECENT_SEARCHES_GROUP_ID,
      title: _t('Recent searches'),
      children: recentlySearchedItems.slice(0, 3),
    });
  }

  if (aiPromptsGroup) {
    groups.push(aiPromptsGroup);
  }

  const trendingLimit = isMobileOrTabletView ? TRENDING_RESULTS_LIMIT_MOBILE_TABLET : TRENDING_RESULTS_LIMIT_DESKTOP;
  const trendingElements = trendingSuggestions?.[0]?.elements || [];
  const limitedElements = trendingLimit ? trendingElements.slice(0, trendingLimit) : trendingElements;
  if (limitedElements.length > 0) {
    groups.push({
      id: TRENDING_SEARCHES_GROUP_ID,
      title: _t('Trending on Coursera'),
      children: mapSearchSuggestionHitsToAutocompleteItems(limitedElements, TRENDING_SEARCHES_GROUP_ID),
    });
  }

  // Add recently viewed items or trending products section
  // Mobile/tablet (< 1061px): show 4 items (128px cards, displayed side by side)
  // Desktop breakpoints: use screenWidth to determine items
  // - 1061px-1280px: 2 items (246px cards)
  // - 1281px-1699px: 3 items (246px cards)
  // - 1700px+: 4 items (246px cards)
  const hasRecentlyViewedItems = recentlyViewedItems && recentlyViewedItems.length > 0;
  const hasTrendingProducts =
    trendingProducts && trendingProducts[0]?.elements && trendingProducts[0].elements.length > 0;

  if (hasRecentlyViewedItems || hasTrendingProducts) {
    let maxRecentlyViewedItems: number;
    if (isMobileOrTabletView) {
      // Mobile/tablet: show 4 items
      maxRecentlyViewedItems = 4;
    } else if (screenWidth !== undefined) {
      // Desktop: use screenWidth for breakpoints
      if (screenWidth >= 1061 && screenWidth <= 1280) {
        maxRecentlyViewedItems = 2;
      } else if (screenWidth >= 1281 && screenWidth < 1700) {
        maxRecentlyViewedItems = 3;
      } else {
        // 1700px+: 4 items
        maxRecentlyViewedItems = 4;
      }
    } else {
      // Fallback: default to 4 items
      maxRecentlyViewedItems = 4;
    }

    let itemsToShow: AutocompleteItems = [];

    if (hasRecentlyViewedItems) {
      const slicedRecentlyViewedItems = recentlyViewedItems.slice(0, maxRecentlyViewedItems);
      itemsToShow = [...slicedRecentlyViewedItems];

      // If we have less than 4 recently viewed items but more than 0, append trending products to reach at least 4 items
      if (slicedRecentlyViewedItems.length > 0 && slicedRecentlyViewedItems.length < 4 && hasTrendingProducts) {
        const productElements = trendingProducts[0]?.elements || [];
        const neededItems = 4 - slicedRecentlyViewedItems.length;
        const allTrendingProductItems = mapSearchHitProductsToAutocompleteItems(productElements);

        // Remove duplicates by name - filter out trending products that have the same name as recently viewed items
        const recentlyViewedNames = new Set(slicedRecentlyViewedItems.map((item) => item.name.toLowerCase()));
        const uniqueTrendingProductItems = allTrendingProductItems.filter(
          (item) => !recentlyViewedNames.has(item.name.toLowerCase())
        );

        // Take only the needed number of unique items
        const trendingProductItems = uniqueTrendingProductItems.slice(0, neededItems);
        itemsToShow = [...slicedRecentlyViewedItems, ...trendingProductItems];
      }
    } else if (hasTrendingProducts) {
      // No recently viewed items, but we have trending products - show them
      const productElements = trendingProducts[0]?.elements || [];
      const allTrendingProductItems = mapSearchHitProductsToAutocompleteItems(productElements);
      itemsToShow = allTrendingProductItems.slice(0, maxRecentlyViewedItems);
    }

    // Apply the same screen width-based slicing logic to the final merged items
    // This ensures the final itemsToShow respects the same max items logic as maxRecentlyViewedItems
    itemsToShow = itemsToShow.slice(0, maxRecentlyViewedItems);
    if (itemsToShow.length > 0) {
      // Determine group ID and title based on whether all items are recently viewed
      const allItemsAreRecentlyViewed = itemsToShow.every((item) => item.isRecentlyViewed === true);
      const groupId = allItemsAreRecentlyViewed ? RECENTLY_VIEWED_GROUP_ID : LEARNER_FAVORITES_GROUP_ID;
      const title = allItemsAreRecentlyViewed ? _t('Recently viewed') : _t('Start with these learner favorites');

      groups.push({
        id: groupId,
        title,
        children: itemsToShow,
      });
    }
  }

  // Add onboarding if user is not logged in
  if (!isUserLoggedIn && onboardingUrl) {
    const onboardingItems = getOnboardingAutocompleteItems(onboardingUrl);
    groups.push({
      id: ONBOARDING_GROUP_ID,
      title: '',
      children: onboardingItems,
    });
  }

  return groups;
}

function getSuggestionSectionAndType(
  sectionPrefix: string | undefined,
  displayedQuery: string,
  recentlyViewedItems?: AutocompleteItems
) {
  let suggestionSection: SuggestionSection = 'suggested_searches';
  let suggestionType: SuggestionType = 'search_query';
  const partnerMatch = isExactPartnerNameMatch(displayedQuery);
  switch (sectionPrefix) {
    case AUTOCOMPLETE_SECTION_PREFIXES.RECENT_SEARCHES:
      suggestionSection = 'recent_searches';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.SEE_ALL_TEXT:
      suggestionType = 'all_results_for';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED:
      suggestionType = 'product';
      suggestionSection = recentlyViewedItems?.some((item) => !item.isRecentlyViewed)
        ? 'learner_favorites'
        : 'recently_viewed';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.TRENDING_SEARCHES:
      suggestionSection = 'trending';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES:
      suggestionType = 'product';
      suggestionSection = 'direct_matches';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING:
      suggestionType = 'onboarding_link';
      suggestionSection = 'redirect_links';
      break;
    case AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_AS_YOU_TYPE:
    case AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_ZERO_STATE:
      suggestionType = 'ai_prompt';
      suggestionSection = 'ai_prompt';
      break;
    default:
      break;
  }
  if (partnerMatch) {
    suggestionType = 'partner';
  }

  return { suggestionSection, suggestionType };
}

function findRecentlyViewedProduct(recentlyViewedItems: AutocompleteItems, displayedQuery: string): Product | null {
  const recentlyViewedItemMatch = recentlyViewedItems.find((item) => item.name === displayedQuery);
  const productId = recentlyViewedItemMatch?.eventProductId?.split(AUTOCOMPLETE_PREFIX_DELIMITER).pop();
  if (productId && recentlyViewedItemMatch) {
    return {
      id: productId,
      name: recentlyViewedItemMatch.name,
      slug: recentlyViewedItemMatch.path,
    };
  }
  return null;
}

function hasRecentlyViewedXDPProduct(
  recentlyViewedItems: AutocompleteItems,
  displayedQuery: string
): AutocompleteItem | null {
  const recentlyViewedItemMatch = recentlyViewedItems.find((item) => item.name === displayedQuery);
  const productId = recentlyViewedItemMatch?.eventProductId?.split(AUTOCOMPLETE_PREFIX_DELIMITER).pop();
  if (productId && recentlyViewedItemMatch) {
    return recentlyViewedItemMatch;
  }
  return null;
}

function getAssociatedPartner(displayedQuery: string): Partner | null {
  if (isExactPartnerNameMatch(displayedQuery)) {
    const partnerMatch = getTop10PartnerOptions().find(({ name }) => isLoosePartnerNameMatch(name, displayedQuery));
    if (partnerMatch) {
      return { primaryPartnerId: partnerMatch.partnerId };
    }
  }
  return null;
}

function getSuggestionState(autocompleteMode: AutocompleteMode): SuggestionState {
  switch (autocompleteMode) {
    case AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE:
      return 'zero_state';
    case AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE:
      return 'as_you_type';
    default:
      return 'zero_state';
  }
}
export function getAutocompleteEventingData(
  suggestionItem: string,
  autocompleteMode: AutocompleteMode,
  recentlyViewedItems: AutocompleteItems,
  suggestionIndex?: number
) {
  const sectionPrefix = autocompletePrefixes.find((prefix) => suggestionItem.startsWith(prefix));
  const displayedQuery = sectionPrefix ? suggestionItem.replace(sectionPrefix, '') : suggestionItem;
  const searchSuggestions = displayedQuery;

  const { suggestionSection, suggestionType } = getSuggestionSectionAndType(
    sectionPrefix,
    displayedQuery,
    recentlyViewedItems
  );
  const product = findRecentlyViewedProduct(recentlyViewedItems, displayedQuery);
  const matchedItem = hasRecentlyViewedXDPProduct(recentlyViewedItems, displayedQuery);
  const associatedPartner = getAssociatedPartner(displayedQuery);
  const suggestionState = getSuggestionState(autocompleteMode);
  const result: ClickAutocompleteSuggestions = {
    suggestionState,
    suggestionSection,
    suggestionType,
    searchSuggestions,
    suggestionIndex,
    page: {},
  };

  if (typeof suggestionIndex !== 'undefined') {
    result.suggestionIndex = suggestionIndex;
  }

  if (associatedPartner) {
    result.associatedPartner = associatedPartner;
  }

  if (product) {
    result.product = product;
  }

  if (matchedItem) {
    result.hasRecentlyViewedXDP = !!matchedItem.isRecentlyViewed;
  }

  return result;
}
const getAutocompleteLegacyProduct = (hit: Hits | SuggestionHits): Product | null => {
  // for producthit id,name,objectUrl for discocollection its id, name, objectUrl for recentlyViewed, objectId, name, objectUrl
  if (
    (hit.__typename === 'Search_ProductHit' ||
      hit.__typename === 'DiscoveryCollections_professionalCertificate' ||
      hit.__typename === 'DiscoveryCollections_specialization') &&
    hit.id
  ) {
    return {
      id: hit.id,
      name: hit.name,
      slug: hit.objectUrl,
    };
  }
  if (hit.trackingName === 'zero_state_product') {
    return {
      id: hit.objectID || '',
      name: hit.name,
      slug: hit.objectUrl,
    };
  }
  return null;
};

export const getLegacyAutocompleteInfo = (hitSection: string) => {
  let suggestionSection: SuggestionSection;
  let suggestionType: SuggestionType;

  switch (hitSection) {
    case 'most_popular_specializations':
      suggestionSection = 'most_popular_specializations';
      suggestionType = 'product';
      break;

    case 'popular_right_now':
      suggestionSection = 'popular_right_now';
      suggestionType = 'search_query';
      break;

    case 'popular_searches':
      suggestionSection = 'popular_searches';
      suggestionType = 'search_query';
      break;

    case 'recent_searches':
      suggestionSection = 'recent_searches';
      suggestionType = 'search_query';
      break;

    case 'recently_viewed':
      suggestionSection = 'recently_viewed';
      suggestionType = 'product';
      break;

    case 'redirect_links':
      suggestionSection = 'redirect_links';
      suggestionType = 'onboarding_link';
      break;

    default:
      suggestionSection = 'suggested_searches';
      suggestionType = 'search_query';
      break;
  }

  return { suggestionSection, suggestionType };
};

export function getAutocompleteLegacyEventingData(hit: Hits | SuggestionHits, query: string) {
  const searchSuggestions = hit.name;
  const product = getAutocompleteLegacyProduct(hit);
  const { suggestionSection, suggestionType } = getLegacyAutocompleteInfo(hit.sectionId || '');

  const result: ClickAutocompleteSuggestions = {
    suggestionState: query ? 'as_you_type' : 'zero_state',
    suggestionSection,
    suggestionType,
    searchSuggestions,
    suggestionIndex: hit.hitPosition,
    page: {},
  };
  if (hit.indexName) {
    result.searchIndexName = hit.indexName;
  }
  if (product) {
    result.product = product;
    result.suggestionType = 'product';
    if (suggestionSection === 'suggested_searches') {
      result.suggestionSection = 'direct_matches';
    }
  }
  return result;
}
